import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { CircleAlert, FileQuestion, HardDrive, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { ApiError, driveApi, type DriveItem } from "@/api/driveApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { Button } from "@/components/ui/button";
import { EmptyState } from "./EmptyState";

type State =
    | { status: "resolving" }
    | { status: "denied" }
    | { status: "trashed"; item: DriveItem }
    | { status: "error"; message: string };

const enc = encodeURIComponent;

/**
 * Where an item opens for the caller:
 * - a folder → the folder;
 * - their own file → its folder, selected and previewed;
 * - someone else's file → its folder when that folder is open to them too,
 *   otherwise Shared with me — selected and previewed either way.
 */
async function targetFor(item: DriveItem, token: string | undefined): Promise<string> {
    if (item.kind === "Folder") return `/folder/${enc(item.id)}`;
    const select = `?select=${enc(item.id)}&open=1`;
    if (!item.ownerEmail) return `${item.parentId ? `/folder/${enc(item.parentId)}` : "/"}${select}`;
    if (item.parentId) {
        try {
            const parent = await driveApi.getItem(item.parentId, token);
            if (parent.kind === "Folder" && !parent.isTrashed) return `/folder/${enc(parent.id)}${select}`;
        } catch {
            /* the folder isn't shared with the caller — only the file is */
        }
    }
    return `/shared-with-me${select}`;
}

/**
 * /open/:itemId — the universal link to a Drive item (Share dialog's "Copy
 * link", the item menu's "Copy link", Mail and Connect attachments). Works
 * out where the item lives for whoever opens it and redirects there; people
 * without access get a friendly dead end instead of an empty folder.
 */
export function OpenItemPage() {
    const { itemId } = useParams();
    const navigate = useNavigate();
    const auth = useAuth();
    const token = useAccessToken();
    const tokenRef = useRef(token);
    tokenRef.current = token;
    const hasToken = Boolean(token);
    const [state, setState] = useState<State>({ status: "resolving" });
    const [attempt, setAttempt] = useState(0);

    useEffect(() => {
        if (!itemId) {
            setState({ status: "denied" });
            return;
        }
        if (!hasToken) return;
        let cancelled = false;
        setState({ status: "resolving" });
        void (async () => {
            try {
                const item = await driveApi.getItem(itemId, tokenRef.current);
                if (cancelled) return;
                // Only the owner sees a binned item — send them to the bin.
                if (item.isTrashed) {
                    setState({ status: "trashed", item });
                    return;
                }
                const target = await targetFor(item, tokenRef.current);
                if (!cancelled) navigate(target, { replace: true });
            } catch (err) {
                if (cancelled) return;
                // 404 covers "deleted", "never existed", "not a valid id" and
                // "not shared with you" alike — the BFF doesn't tell them apart.
                if (err instanceof ApiError && (err.status === 404 || err.status === 403 || err.status === 400)) {
                    setState({ status: "denied" });
                } else {
                    setState({ status: "error", message: err instanceof Error ? err.message : String(err) });
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [itemId, hasToken, attempt, navigate]);

    const email = auth.user?.profile.email;

    let body;
    if (state.status === "resolving") {
        body = (
            <div className="flex flex-col items-center justify-center gap-3 py-24 text-sm text-muted-foreground" role="status">
                <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
                Opening…
            </div>
        );
    } else if (state.status === "trashed") {
        body = (
            <EmptyState
                icon={Trash2}
                tone="amber"
                title={`“${state.item.name}” is in your bin`}
                description="Restore it from the bin to open it again."
            >
                <Button onClick={() => navigate("/trash", { replace: true })}>
                    <Trash2 className="h-4 w-4" /> Go to bin
                </Button>
                <Button variant="outline" onClick={() => navigate("/", { replace: true })}>
                    <HardDrive className="h-4 w-4" /> My Drive
                </Button>
            </EmptyState>
        );
    } else if (state.status === "error") {
        body = (
            <EmptyState icon={CircleAlert} tone="rose" title="Couldn't open this link" description={state.message}>
                <Button variant="outline" onClick={() => setAttempt((n) => n + 1)}>
                    <RotateCcw className="h-4 w-4" /> Try again
                </Button>
                <Button onClick={() => navigate("/", { replace: true })}>
                    <HardDrive className="h-4 w-4" /> My Drive
                </Button>
            </EmptyState>
        );
    } else {
        body = (
            <EmptyState
                icon={FileQuestion}
                tone="slate"
                title="You don't have access to this file or it was deleted"
                description={
                    <>
                        Ask the owner to share it with you.
                        {email ? (
                            <span className="mt-2 block">
                                You're signed in as <span className="font-medium text-foreground">{email}</span> — if it was
                                shared with a different account, sign in with that one.
                            </span>
                        ) : null}
                    </>
                }
            >
                <Button onClick={() => navigate("/", { replace: true })}>
                    <HardDrive className="h-4 w-4" /> Go to My Drive
                </Button>
            </EmptyState>
        );
    }

    return <div className="flex-1 overflow-auto">{body}</div>;
}
